/**
 * API client for EventFlow backend
 * Uses zustand store token for auth, with localStorage fallback during hydration
 */
import axios from 'axios';
import { useAuthStore } from './store';
import { getToken } from '@/lib/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Safely extract error message from API response
 * Handles both string and Pydantic validation error formats
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  
  if (!detail) return fallback;
  
  // If detail is a string, return it directly
  if (typeof detail === 'string') return detail;
  
  // If detail is an array (Pydantic validation errors), extract messages
  if (Array.isArray(detail)) {
    const messages = detail.map(err => {
      if (typeof err === 'string') return err;
      if (err.msg) return err.msg;
      return JSON.stringify(err);
    });
    return messages.join(', ') || fallback;
  }
  
  // If detail is an object with a message
  if (detail.msg) return detail.msg;
  if (detail.message) return detail.message;
  
  return fallback;
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
// Reads from zustand store first, then persisted store, then fallback localStorage
api.interceptors.request.use((config) => {
  // DEFENSIVE URL NORMALISATION: baseURL already contains `/api`, so a call
  // like `api.get('/api/school/...')` would produce `/api/api/school/...` and
  // 404. Strip any accidental leading `/api` so both styles work safely.
  if (config.url && typeof config.url === 'string') {
    if (config.url.startsWith('/api/')) {
      config.url = config.url.slice(4);  // drop the leading `/api`
    } else if (config.url === '/api') {
      config.url = '/';
    }
  }

  // School portal "View-As" preview overrides the regular admin token so
  // shared pages (Bible reader, give, etc.) that use the axios `api` instance
  // authenticate as the previewed student. Without this branch the Bible
  // reading-plan reader, /give and any cross-module pages would fall back to
  // the admin token and either 401 or return the wrong user's data.
  let token = '';
  try {
    token = sessionStorage.getItem('school_preview_token') || '';
  } catch (_e) { /* sessionStorage unavailable */ }

  if (!token) {
    // Try zustand state first (available after hydration)
    token = useAuthStore.getState().token;
  }
  
  // If not available, try to read from persisted localStorage directly
  // This handles the race condition before zustand hydrates
  if (!token) {
    try {
      const persistedAuth = localStorage.getItem('eventflow-auth');
      if (persistedAuth) {
        const parsed = JSON.parse(persistedAuth);
        token = parsed?.state?.token;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  // Final fallback to direct token key (set during login)
  if (!token) {
    token = getToken();
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // FormData uploads MUST NOT carry the default `application/json` Content-Type
  // — axios will JSON.stringify FormData (yielding "{}") and the server will
  // 422 every single request. The browser/axios sets the correct
  // `multipart/form-data; boundary=…` when we strip the default header here.
  // This is the systemic fix for the milestone/gallery/upload "Save not
  // clickable / nothing happens" bug found in iteration 285.
  try {
    const isFormData =
      (typeof FormData !== 'undefined' && config.data instanceof FormData);
    if (isFormData && config.headers) {
      // Axios stores headers in a couple of shapes depending on version;
      // delete every place a default Content-Type might live.
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
      if (config.headers.common) {
        delete config.headers.common['Content-Type'];
        delete config.headers.common['content-type'];
      }
      const method = (config.method || 'get').toLowerCase();
      if (config.headers[method]) {
        delete config.headers[method]['Content-Type'];
        delete config.headers[method]['content-type'];
      }
    }
  } catch (e) {
    // never block a request because of header normalisation
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login on 401 if:
    // 1. Not already on login page
    // 2. Not on public org pages (member login modal)
    // 3. Not on member portal pages
    // 4. Not on public landing/home page
    // 5. The error is from an authenticated admin endpoint
    const pathname = window.location.pathname;
    const isLoginPage = pathname.includes('/login');
    const isPublicOrgPage = pathname.startsWith('/org/');
    const isMemberPage = pathname.startsWith('/member');
    const isBiblePage = pathname.startsWith('/bible');
    // The security portal keeps its own token; one 401 from a screen the
    // officer's role does not open must not sign them out of the gate.
    const isSecurityPortal = pathname.startsWith('/security');
    const isPublicPage = pathname === '/' || pathname.startsWith('/churches') || pathname.startsWith('/page/') || pathname.startsWith('/event') || pathname.startsWith('/campaigns');
    const isMemberLoginEndpoint = error.config?.url?.includes('/member/login');
    const isBibleEndpoint = error.config?.url?.includes('/bible/');
    
    if (error.response?.status === 401 && !isLoginPage && !isPublicOrgPage && !isMemberPage && !isPublicPage && !isMemberLoginEndpoint && !isBiblePage && !isBibleEndpoint && !isSecurityPortal) {
      // Clear auth state and redirect to the most appropriate login page so
      // operators / chasers don't land on the super-admin sign-in screen.
      useAuthStore.getState().logout();
      localStorage.removeItem('eventflow_token');
      localStorage.removeItem('eventflow-auth');
      let loginTarget = '/login';
      if (pathname === '/studio' || pathname.startsWith('/studio/')) {
        loginTarget = '/studio/login';
      } else if (pathname === '/my-campaigns' || pathname.startsWith('/my-campaigns/')) {
        loginTarget = '/campaigns/login';
      }
      window.location.href = loginTarget;
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  adminLogin: (data) => api.post('/admin/login', data),
  delegateLogin: (data) => api.post('/delegate/login', data),
  getAdminProfile: () => api.get('/admin/me'),
  getDelegateProfile: () => api.get('/delegate/me'),
  updateDelegateProfile: (data) => api.put('/delegate/me', data),
  demoLogin: (userType) => api.post('/demo/login', null, { params: { user_type: userType } }),
};

// Payment APIs
export const paymentAPI = {
  // Stripe checkout
  createCheckout: (data) => api.post('/stripe/checkout/create', data),
  getStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
  
  // Admin: Payment stats and transactions
  getStats: (eventId) => api.get(`/admin/events/${eventId}/payments/stats`),
  getTransactions: (eventId) => api.get(`/admin/events/${eventId}/payments`),
  
  // Delegate: Get my payment plan
  getMyPaymentPlan: () => api.get('/delegate/payment-plan'),
};

// Custom Payment Gateway APIs
export const customPaymentAPI = {
  // Public: Get available gateways for event
  getGateways: (eventId) => api.get(`/events/${eventId}/payment-gateways`),
  
  // Initialize payment
  initializePayment: (eventId, data) => api.post(`/events/${eventId}/payments/initialize`, data),
  
  // Confirm bank transfer/manual payment
  confirmPayment: (eventId, paymentId, data) => api.post(`/events/${eventId}/payments/${paymentId}/confirm`, data),
  
  // Verify payment status
  verifyPayment: (eventId, paymentId) => api.get(`/events/${eventId}/payments/${paymentId}/verify`),
  
  // Get payment receipt
  getReceipt: (eventId, paymentId) => api.get(`/events/${eventId}/payments/${paymentId}/receipt`),
  
  // Admin: Configure gateways
  configureGateway: (data) => api.post('/admin/payment-gateways', data),
  updateGateway: (gatewayId, data) => api.put(`/admin/payment-gateways/${gatewayId}`, data),
  deleteGateway: (gatewayId) => api.delete(`/admin/payment-gateways/${gatewayId}`),
  toggleGateway: (gatewayId, isActive) => api.patch(`/admin/payment-gateways/${gatewayId}`, { is_active: isActive }),
  
  // Admin: Get all payments for event
  getPayments: (eventId, status) => api.get(`/admin/events/${eventId}/payments`, { params: { status } }),
  
  // Admin: Manual confirmation
  adminConfirmPayment: (paymentId, data) => api.post(`/admin/payments/${paymentId}/confirm`, data),
  
  // Installment payment
  payInstallment: (eventId, paymentId) => api.post(`/events/${eventId}/payments/${paymentId}/pay-installment`),
  
  // Get installment schedule
  getInstallmentSchedule: (eventId, paymentId) => api.get(`/events/${eventId}/payments/${paymentId}/installments`),
};

// Event APIs
export const eventAPI = {
  list: (activeOnly = true) => api.get('/events', { params: { active_only: activeOnly } }),
  listForAdmin: () => api.get('/admin/events'),  // Filtered by admin access
  publish: (eventId, published) => api.post(`/admin/events/${eventId}/publish`, { published }),
  feature: (eventId, featured) => api.post(`/admin/events/${eventId}/feature`, { published: featured }),
  // iter 619 — takedowns go by POST through the same axios client as publish
  // and feature. The raw `fetch` + DELETE / PUT versions never reached the
  // backend in production (the edge answered 2xx itself) and the office was
  // told the event was gone while it stayed on the public calendar.
  remove: (eventId) => api.post(`/admin/events/${eventId}/delete`),
  archive: (eventId, archived) =>
    api.post(`/admin/events/${eventId}/archive`, null, { params: { archived } }),
  setVisibility: (eventId, visible) =>
    api.post(`/admin/events/${eventId}/visibility`, null, { params: { visible } }),
  get: (eventId) => api.get(`/events/${eventId}`),
  create: (data) => api.post('/admin/events', data),
  update: (eventId, data) => api.put(`/events/${eventId}`, data),
  // iter 635 — an annual conference is one event plus a rule; these read and
  // manage the runs it generates.
  occurrences: (eventId, params = {}) => api.get(`/events/${eventId}/occurrences`, { params }),
  cancelOccurrence: (eventId, day) => api.post(`/admin/events/${eventId}/occurrences/${day}/cancel`),
  restoreOccurrence: (eventId, day) => api.delete(`/admin/events/${eventId}/occurrences/${day}/cancel`),
  activateOccurrence: (eventId, day) => api.post(`/admin/events/${eventId}/occurrences/${day}/activate`),
  updateBranding: (eventId, data) => api.put(`/admin/events/${eventId}/branding`, data),
  // Church / province tagging — who sees this event in their workspace
  getOrgTags: (eventId) => api.get(`/admin/events/${eventId}/org-tags`),
  updateOrgTags: (eventId, unitIds) => api.put(`/admin/events/${eventId}/org-tags`, { unit_ids: unitIds }),
  listForChurch: (churchId) => api.get(`/admin/churches/${churchId}/events`),
  getTiers: (eventId) => api.get(`/events/${eventId}/tiers`),
  getAdminTiers: (eventId) => api.get(`/events/${eventId}/tiers`, { params: { include_hidden: true } }),
  addTier: (eventId, data) => api.post(`/admin/events/${eventId}/tiers`, data),
  updateTier: (eventId, tierId, data) => api.put(`/admin/events/${eventId}/tiers/${tierId}`, data),
  deleteTier: (eventId, tierId) => api.delete(`/admin/events/${eventId}/tiers/${tierId}`),
  // File upload for branding assets
  uploadBrandingAsset: (eventId, assetType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/events/${eventId}/upload/${assetType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Registration APIs
export const registrationAPI = {
  register: (eventId, data) => api.post(`/events/${eventId}/register`, data),
  registerFamily: (eventId, data) => api.post(`/events/${eventId}/register/family`, data),
  getFamilyGroup: (eventId, familyGroupId) => api.get(`/events/${eventId}/family/${familyGroupId}`),
  mockPayment: (eventId, delegateId, idempotencyKey) =>
    api.post(`/events/${eventId}/payment/mock`, null, {
      params: { delegate_id: delegateId, idempotency_key: idempotencyKey },
    }),
};

// Payment APIs
export const paymentMethodsAPI = {
  getPublicMethods: (eventId) => api.get(`/events/${eventId}/payment-methods`),
  getAdminMethods: (eventId) => api.get(`/admin/events/${eventId}/payment-methods`),
  addMethod: (eventId, data) => api.post(`/admin/events/${eventId}/payment-methods`, data),
  updateMethod: (eventId, methodId, data) => api.put(`/admin/events/${eventId}/payment-methods/${methodId}`, data),
  deleteMethod: (eventId, methodId) => api.delete(`/admin/events/${eventId}/payment-methods/${methodId}`),
  submitPayment: (eventId, data) => api.post(`/events/${eventId}/payments/submit`, data),
  verifyPayment: (eventId, paymentId, data) => api.put(`/admin/events/${eventId}/payments/${paymentId}/verify`, data),
  listPayments: (eventId, status = null) => api.get(`/admin/events/${eventId}/payments`, { params: { status } }),
};

// Delegate APIs
export const delegateAPI = {
  getBadge: () => api.get('/delegate/badge'),
  getCoinBalance: (eventId) => api.get(`/events/${eventId}/coins/balance`),
  getCoinHistory: (eventId, limit = 50) => api.get(`/events/${eventId}/coins/transactions`),
  transferCoins: (eventId, data) => api.post(`/events/${eventId}/coins/transfer`, data),
  getNetworkingDelegates: (eventId) => api.get(`/events/${eventId}/networking/delegates`),
  getNetworkingSuggestions: () => api.get('/delegate/networking/suggestions'),
  getIcebreaker: (otherId) => api.get(`/delegate/networking/icebreaker/${otherId}`),
  askConcierge: (question) => api.post('/delegate/concierge/ask', null, { params: { question } }),
};

// Check-in APIs
export const checkinAPI = {
  checkIn: (eventId, qrData, gate = 'main') =>
    api.post(`/events/${eventId}/checkin`, null, { params: { qr_data: qrData, gate } }),
  getGateStatus: (eventId, gate = 'main') =>
    api.get(`/events/${eventId}/gate-status`, { params: { gate } }),
};

// Meal APIs
export const mealAPI = {
  // Public
  list: (eventId) => api.get(`/events/${eventId}/meals`),
  // Admin
  adminList: (eventId) => api.get(`/admin/events/${eventId}/meals`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/meals`, data),
  update: (eventId, mealId, data) => api.put(`/admin/events/${eventId}/meals/${mealId}`, data),
  delete: (eventId, mealId) => api.delete(`/admin/events/${eventId}/meals/${mealId}`),
  // Meal Scanning
  checkScanStatus: (eventId, mealId, delegateId) => api.get(`/events/${eventId}/meals/${mealId}/scan-status/${delegateId}`),
  scan: (eventId, mealId, data) => api.post(`/events/${eventId}/meals/${mealId}/scan`, data),
  getScans: (eventId, mealId) => api.get(`/admin/events/${eventId}/meals/${mealId}/scans`),
  getScansSummary: (eventId) => api.get(`/admin/events/${eventId}/meal-scans/summary`),
  // Legacy Operations
  redeem: (eventId, delegateId, day, category) =>
    api.post(`/events/${eventId}/meals/redeem`, null, {
      params: { delegate_id: delegateId, day, category },
    }),
  scanRedeem: (eventId, qrData, day, category) =>
    api.post(`/events/${eventId}/meals/scan`, null, {
      params: { qr_data: qrData, day, category },
    }),
};

// Stock Scanning APIs
export const stockScanAPI = {
  lookup: (eventId, barcode) => api.get(`/admin/events/${eventId}/finance/stock/lookup`, { params: { barcode } }),
  scan: (eventId, data) => api.post(`/admin/events/${eventId}/finance/stock/scan`, data),
  getAllTransactions: (eventId, params) => api.get(`/admin/events/${eventId}/finance/stock/all-transactions`, { params }),
  getLowStock: (eventId) => api.get(`/admin/events/${eventId}/finance/stock/low-stock`),
  deductForMeal: (eventId, data) => api.post(`/admin/events/${eventId}/finance/stock/meal-deduction`, data),
  getMealUsage: (eventId, params) => api.get(`/admin/events/${eventId}/finance/stock/meal-usage`, { params }),
};

// Currency APIs
export const currencyAPI = {
  getAll: () => api.get('/currencies'),
  getEventCurrency: (eventId) => api.get(`/admin/events/${eventId}/currency`),
  setEventCurrency: (eventId, currencyCode) => api.put(`/admin/events/${eventId}/currency`, null, { params: { currency_code: currencyCode } }),
};

// Admin delegate management
export const adminDelegateAPI = {
  list: (eventId, status) => api.get(`/events/${eventId}/delegates`, { params: { status } }),
  get: (eventId, delegateId) => api.get(`/events/${eventId}/delegates/${delegateId}`),
  mintCoins: (eventId, delegateId, amount, reason) =>
    api.post(`/admin/events/${eventId}/delegates/${delegateId}/coins`, {
      amount,
      reason,
      type: 'award'
    }),
};

// Incident APIs
export const incidentAPI = {
  report: (eventId, data) => api.post(`/events/${eventId}/incidents`, data),
  list: (eventId) => api.get(`/events/${eventId}/incidents`),
  update: (eventId, incidentId, data) => api.put(`/events/${eventId}/incidents/${incidentId}`, data),
};

// Research APIs
export const researchAPI = {
  speaker: (name) => api.get('/admin/research/speaker', { params: { name } }),
  sponsor: (name) => api.get('/admin/research/sponsor', { params: { name } }),
};

// Housing APIs
export const housingAPI = {
  list: (eventId) => api.get(`/events/${eventId}/housing/units`),
  create: (eventId, data) => api.post(`/events/${eventId}/housing/units`, data),
  assign: (eventId, unitId, delegateId) => api.post(`/events/${eventId}/housing/units/${unitId}/assign`, { delegate_id: delegateId }),
  unassign: (eventId, unitId) => api.post(`/events/${eventId}/housing/units/${unitId}/unassign`),
};

// Custom Registration Form Fields APIs
export const customFieldsAPI = {
  // Public endpoint - for registration form
  list: (eventId) => api.get(`/events/${eventId}/custom-fields`),
  // Admin endpoints
  listAdmin: (eventId) => api.get(`/admin/events/${eventId}/custom-fields`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/custom-fields`, data),
  update: (eventId, fieldId, data) => api.put(`/admin/events/${eventId}/custom-fields/${fieldId}`, data),
  delete: (eventId, fieldId) => api.delete(`/admin/events/${eventId}/custom-fields/${fieldId}`),
  reorder: (eventId, fieldIds) => api.put(`/admin/events/${eventId}/custom-fields/reorder`, fieldIds),
};

// Google Forms / external form integration
export const externalFormAPI = {
  publicGet: (eventId) => api.get(`/events/${eventId}/external-form`),
  getConfig: (eventId) => api.get(`/admin/events/${eventId}/external-form`),
  saveConfig: (eventId, data) => api.put(`/admin/events/${eventId}/external-form`, data),
  preview: (eventId, data) => api.post(`/admin/events/${eventId}/external-form/preview`, data),
  importFields: (eventId, data) => api.post(`/admin/events/${eventId}/external-form/import`, data),
};

// Audit APIs
export const auditAPI = {
  getLogs: (eventId, action, limit = 100) =>
    api.get(`/events/${eventId}/audit-logs`, { params: { action, limit } }),
};

// System Settings APIs (Super Admin)
export const systemAPI = {
  getSettings: () => api.get('/admin/system/settings'),
  updateSettings: (section, data) => api.put('/admin/system/settings', { section, data }),
  testConnection: (service) => api.post(`/admin/system/test/${service}`),
  sendTestEmail: (toEmail) => api.post('/admin/system/test-email', { to_email: toEmail }),
  testTelegram: () => api.post('/admin/system/test-telegram'),
  testWhatsApp: () => api.post('/admin/system/test-whatsapp'),
  testSMS: (data) => api.post('/admin/system/test-sms', data),
};

// Bulk Messaging APIs
export const bulkMessagingAPI = {
  // Multi-channel broadcast to event delegates
  broadcast: (eventId, data) => api.post(`/admin/events/${eventId}/broadcast`, data),
  // Telegram channel broadcast
  telegramBroadcast: (data) => api.post('/admin/telegram/broadcast', data),
  // WhatsApp single message
  sendWhatsApp: (data) => api.post('/admin/whatsapp/send', data),
};

// Dashboard Stats API
export const dashboardAPI = {
  getStats: () => api.get('/admin/dashboard/stats'),
};

// Finance & Budget Management APIs
export const financeAPI = {
  // Summary
  getSummary: (eventId, opts = {}) => api.get(`/admin/events/${eventId}/finance/summary`, opts),
  
  // Funds
  listFunds: (eventId) => api.get(`/admin/events/${eventId}/finance/funds`),
  createFund: (eventId, data) => api.post(`/admin/events/${eventId}/finance/funds`, data),
  updateFund: (eventId, fundId, data) => api.put(`/admin/events/${eventId}/finance/funds/${fundId}`, data),
  deleteFund: (eventId, fundId) => api.delete(`/admin/events/${eventId}/finance/funds/${fundId}`),
  
  // Income/Donations
  listIncome: (eventId, params) => api.get(`/admin/events/${eventId}/finance/income`, { params }),
  createIncome: (eventId, data) => api.post(`/admin/events/${eventId}/finance/income`, data),
  updateIncome: (eventId, incomeId, data) => api.put(`/admin/events/${eventId}/finance/income/${incomeId}`, data),
  deleteIncome: (eventId, incomeId) => api.delete(`/admin/events/${eventId}/finance/income/${incomeId}`),
  approveIncome: (eventId, incomeId) => api.post(`/admin/events/${eventId}/finance/income/${incomeId}/approve`),
  unapproveIncome: (eventId, incomeId) => api.post(`/admin/events/${eventId}/finance/income/${incomeId}/unapprove`),
  
  // Payers/Donors
  listPayers: (eventId, params) => api.get(`/admin/events/${eventId}/finance/payers`, { params }),
  getPayer: (eventId, payerId) => api.get(`/admin/events/${eventId}/finance/payers/${payerId}`),
  createPayer: (eventId, data) => api.post(`/admin/events/${eventId}/finance/payers`, data),
  updatePayer: (eventId, payerId, data) => api.put(`/admin/events/${eventId}/finance/payers/${payerId}`, data),
  deletePayer: (eventId, payerId) => api.delete(`/admin/events/${eventId}/finance/payers/${payerId}`),
  
  // Expenses
  listExpenses: (eventId, category) => api.get(`/admin/events/${eventId}/finance/expenses`, { params: { category } }),
  createExpense: (eventId, data) => api.post(`/admin/events/${eventId}/finance/expenses`, data),
  updateExpense: (eventId, expenseId, data) => api.put(`/admin/events/${eventId}/finance/expenses/${expenseId}`, data),
  deleteExpense: (eventId, expenseId) => api.delete(`/admin/events/${eventId}/finance/expenses/${expenseId}`),
  approveExpense: (eventId, expenseId) => api.post(`/admin/events/${eventId}/finance/expenses/${expenseId}/approve`),
  unapproveExpense: (eventId, expenseId) => api.post(`/admin/events/${eventId}/finance/expenses/${expenseId}/unapprove`),
  // Ecosystem-wide revenue summary (single source of truth, aggregates all events by default)
  getRevenueSummary: (params) => api.get(`/finance/revenue-summary`, { params }),
  // CSV export
  exportCsvUrl: (eventId, kind = 'all', approved_only = false) =>
    `/admin/events/${eventId}/finance/export.csv?kind=${kind}&approved_only=${approved_only}`,
  
  // Stock/Inventory - Support both old and new signatures
  listStock: (tokenOrEventId, eventIdOrCategory, category) => {
    // New signature: listStock(eventId, category)
    // Old signature: listStock(token, eventId, category) - ignore token
    if (eventIdOrCategory && typeof eventIdOrCategory === 'string' && eventIdOrCategory.length > 10) {
      // Old signature - eventIdOrCategory is actually eventId
      return api.get(`/admin/events/${eventIdOrCategory}/finance/stock`, { params: { category } });
    }
    return api.get(`/admin/events/${tokenOrEventId}/finance/stock`, { params: { category: eventIdOrCategory } });
  },
  createStock: (tokenOrEventId, eventIdOrData, data) => {
    // New signature: createStock(eventId, data)
    // Old signature: createStock(token, eventId, data) - ignore token
    if (data) {
      // Old signature
      return api.post(`/admin/events/${eventIdOrData}/finance/stock`, data);
    }
    return api.post(`/admin/events/${tokenOrEventId}/finance/stock`, eventIdOrData);
  },
  createStockItem: (eventId, data) => api.post(`/admin/events/${eventId}/finance/stock`, data),
  updateStock: (tokenOrEventId, eventIdOrItemId, itemIdOrData, data) => {
    // New signature: updateStock(eventId, itemId, data)
    // Old signature: updateStock(token, eventId, itemId, data) - ignore token
    if (data) {
      // Old signature
      return api.put(`/admin/events/${eventIdOrItemId}/finance/stock/${itemIdOrData}`, data);
    }
    return api.put(`/admin/events/${tokenOrEventId}/finance/stock/${eventIdOrItemId}`, itemIdOrData);
  },
  updateStockItem: (eventId, itemId, data) => api.put(`/admin/events/${eventId}/finance/stock/${itemId}`, data),
  deleteStock: (tokenOrEventId, eventIdOrItemId, itemId) => {
    if (itemId) {
      return api.delete(`/admin/events/${eventIdOrItemId}/finance/stock/${itemId}`);
    }
    return api.delete(`/admin/events/${tokenOrEventId}/finance/stock/${eventIdOrItemId}`);
  },
  deleteStockItem: (eventId, itemId) => api.delete(`/admin/events/${eventId}/finance/stock/${itemId}`),
  recordStockTransaction: (eventId, itemId, data) => api.post(`/admin/events/${eventId}/finance/stock/${itemId}/transaction`, data),
  getStockTransactions: (eventId, itemId) => api.get(`/admin/events/${eventId}/finance/stock/${itemId}/transactions`),
};

// Export APIs
export const exportAPI = {
  delegates: (eventId) => `${API_BASE}/admin/events/${eventId}/export/delegates`,
  registrations: (eventId) => `${API_BASE}/admin/events/${eventId}/export/registrations`,
  analytics: (eventId) => `${API_BASE}/admin/events/${eventId}/export/analytics`,
};

// Translation API
export const translationAPI = {
  translate: (text, targetLanguage, sourceLanguage = 'en') => 
    api.post('/translate', { text, target_language: targetLanguage, source_language: sourceLanguage }),
};

// Messaging/Broadcast APIs
export const messagingAPI = {
  getEnabledChannels: () => api.get('/admin/messaging/channels'),
  broadcast: (eventId, data) => api.post(`/events/${eventId}/broadcast`, data),
  getBroadcastHistory: (eventId, limit = 50) => api.get(`/events/${eventId}/broadcast/history`, { params: { limit } }),
};

// Roles & Permissions APIs
export const rolesAPI = {
  list: () => api.get('/admin/roles'),
  get: (roleId) => api.get(`/admin/roles/${roleId}`),
  create: (data) => api.post('/admin/roles', data),
  update: (roleId, data) => api.put(`/admin/roles/${roleId}`, data),
  delete: (roleId) => api.delete(`/admin/roles/${roleId}`),
  getPermissions: () => api.get('/admin/permissions'),
};

// Team Management APIs
export const teamAPI = {
  list: () => api.get('/admin/team'),
  get: (memberId) => api.get(`/admin/team/${memberId}`),
  create: (data) => api.post('/admin/team', data),
  update: (memberId, data) => api.put(`/admin/team/${memberId}`, data),
  delete: (memberId) => api.delete(`/admin/team/${memberId}`),
  assignEventRole: (memberId, eventId, roleId) => api.put(`/admin/team/${memberId}/event-role`, { event_id: eventId, role_id: roleId }),
  removeEventRole: (memberId, eventId) => api.delete(`/admin/team/${memberId}/event-role/${eventId}`),
  getEventTeam: (eventId) => api.get(`/events/${eventId}/team`),
};

// Invitation APIs
export const invitationAPI = {
  list: () => api.get('/admin/invitations'),
  create: (data) => api.post('/admin/invitations', data),
  resend: (invitationId) => api.post(`/admin/invitations/${invitationId}/resend`),
  revoke: (invitationId) => api.delete(`/admin/invitations/${invitationId}`),
  accept: (token, data) => api.post(`/invitations/accept/${token}`, data),
};

// ============== 10 CRUD Module APIs ==============

// 1. Sponsor Management
export const sponsorAPI = {
  list: (eventId, tier) => api.get(`/events/${eventId}/sponsors`, { params: { tier } }),
  adminList: (eventId, status) => api.get(`/admin/events/${eventId}/sponsors`, { params: { status } }),
  get: (eventId, sponsorId) => api.get(`/events/${eventId}/sponsors/${sponsorId}`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/sponsors`, data),
  update: (eventId, sponsorId, data) => api.put(`/admin/events/${eventId}/sponsors/${sponsorId}`, data),
  delete: (eventId, sponsorId) => api.delete(`/admin/events/${eventId}/sponsors/${sponsorId}`),
  reorder: (eventId, sponsors) => api.post(`/admin/events/${eventId}/sponsors/reorder`, { sponsors }),
  tiers: () => api.get('/sponsors/tiers'),
};

// 2. Exhibitor Management
export const exhibitorAPI = {
  list: (eventId, category) => api.get(`/events/${eventId}/exhibitors`, { params: { category } }),
  adminList: (eventId, status) => api.get(`/admin/events/${eventId}/exhibitors`, { params: { status } }),
  get: (eventId, exhibitorId) => api.get(`/events/${eventId}/exhibitors/${exhibitorId}`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/exhibitors`, data),
  update: (eventId, exhibitorId, data) => api.put(`/admin/events/${eventId}/exhibitors/${exhibitorId}`, data),
  delete: (eventId, exhibitorId) => api.delete(`/admin/events/${eventId}/exhibitors/${exhibitorId}`),
  reorder: (eventId, exhibitors) => api.post(`/admin/events/${eventId}/exhibitors/reorder`, { exhibitors }),
  categories: (eventId) => api.get(`/events/${eventId}/exhibitors/categories`),
};

// 2.1. Exhibitor Booths (Legacy - keeping for backwards compatibility)
export const boothAPI = {
  list: (eventId, floor) => api.get(`/events/${eventId}/booths`, { params: { floor } }),
  get: (eventId, boothId) => api.get(`/events/${eventId}/booths/${boothId}`),
  create: (eventId, data) => api.post(`/events/${eventId}/booths`, data),
  update: (eventId, boothId, data) => api.put(`/events/${eventId}/booths/${boothId}`, data),
  delete: (eventId, boothId) => api.delete(`/events/${eventId}/booths/${boothId}`),
};

// 3. Speaker Management
export const speakerAPI = {
  list: (eventId, status) => api.get(`/events/${eventId}/speakers`, { params: { status } }),
  adminList: (eventId, status) => api.get(`/admin/events/${eventId}/speakers`, { params: { status } }),
  get: (eventId, speakerId) => api.get(`/events/${eventId}/speakers/${speakerId}`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/speakers`, data),
  update: (eventId, speakerId, data) => api.put(`/admin/events/${eventId}/speakers/${speakerId}`, data),
  delete: (eventId, speakerId) => api.delete(`/admin/events/${eventId}/speakers/${speakerId}`),
  reorder: (eventId, speakers) => api.post(`/admin/events/${eventId}/speakers/reorder`, { speakers }),
};

// 4. Session Management
export const sessionAPI = {
  list: (eventId, track, sessionType) => api.get(`/events/${eventId}/sessions`, { params: { track, session_type: sessionType } }),
  get: (eventId, sessionId) => api.get(`/admin/events/${eventId}/sessions/${sessionId}`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/sessions`, data),
  update: (eventId, sessionId, data) => api.put(`/admin/events/${eventId}/sessions/${sessionId}`, data),
  delete: (eventId, sessionId) => api.delete(`/admin/events/${eventId}/sessions/${sessionId}`),
};

// 5. Surveys & Feedback
export const surveyAPI = {
  list: (eventId, activeOnly) => api.get(`/events/${eventId}/surveys`, { params: { active_only: activeOnly } }),
  get: (eventId, surveyId) => api.get(`/events/${eventId}/surveys/${surveyId}`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/surveys`, data),
  update: (eventId, surveyId, data) => api.put(`/admin/events/${eventId}/surveys/${surveyId}`, data),
  delete: (eventId, surveyId) => api.delete(`/admin/events/${eventId}/surveys/${surveyId}`),
  respond: (eventId, surveyId, answers) => api.post(`/events/${eventId}/surveys/${surveyId}/respond`, { answers }),
  getResponses: (eventId, surveyId) => api.get(`/admin/events/${eventId}/surveys/${surveyId}/responses`),
};

// 6. Prize/Raffle Management
export const prizeAPI = {
  list: (eventId) => api.get(`/events/${eventId}/prizes`),
  get: (eventId, prizeId) => api.get(`/events/${eventId}/prizes/${prizeId}`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/prizes`, data),
  update: (eventId, prizeId, data) => api.put(`/admin/events/${eventId}/prizes/${prizeId}`, data),
  delete: (eventId, prizeId) => api.delete(`/admin/events/${eventId}/prizes/${prizeId}`),
  enter: (eventId, prizeId) => api.post(`/events/${eventId}/prizes/${prizeId}/enter`),
  draw: (eventId, prizeId) => api.post(`/admin/events/${eventId}/prizes/${prizeId}/draw`),
};

// 7. Networking Appointments
export const appointmentAPI = {
  list: (eventId) => api.get(`/events/${eventId}/appointments`),
  create: (eventId, data) => api.post(`/events/${eventId}/appointments`, data),
  update: (eventId, appointmentId, data) => api.put(`/events/${eventId}/appointments/${appointmentId}`, data),
  cancel: (eventId, appointmentId) => api.delete(`/events/${eventId}/appointments/${appointmentId}`),
};

// 8. Certificate Management
export const certificateAPI = {
  list: (eventId) => api.get(`/events/${eventId}/certificates`),
  get: (eventId, certId) => api.get(`/events/${eventId}/certificates/${certId}`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/certificates`, data),
  verify: (code) => api.get(`/certificates/verify/${code}`),
  delete: (eventId, certId) => api.delete(`/admin/events/${eventId}/certificates/${certId}`),
  getMine: () => api.get('/delegate/certificates'),
  bulkGenerate: (eventId, data) => api.post(`/admin/events/${eventId}/certificates/bulk-generate`, null, { params: data }),
};

// AI Features
export const aiAPI = {
  // Status
  getStatus: () => api.get('/admin/ai/status'),
  
  // Generate content
  generate: (data) => api.post('/admin/ai/generate', data),
  
  // Event-specific
  generateEventSummary: (eventId) => api.post(`/admin/events/${eventId}/ai/generate-summary`),
  enhanceSession: (eventId, sessionId) => api.post(`/admin/events/${eventId}/sessions/${sessionId}/ai/enhance`),
  generateSpeakerBio: (eventId, speakerId) => api.post(`/admin/events/${eventId}/speakers/${speakerId}/ai/generate-bio`),
  
  // Legacy endpoints (keep for backwards compatibility)
  getConfig: (eventId) => api.get(`/admin/events/${eventId}/ai/config`),
  updateConfig: (eventId, data) => api.put(`/admin/events/${eventId}/ai/config`, data),
  generateSummary: (eventId, sessionId, data) => api.post(`/admin/events/${eventId}/ai/sessions/${sessionId}/summary`, data),
  askQuestion: (eventId, data) => api.post(`/admin/events/${eventId}/ai/ask`, data),
  transcribe: (eventId, sessionId, formData) => api.post(`/admin/events/${eventId}/ai/sessions/${sessionId}/transcribe`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getTranscript: (eventId, sessionId) => api.get(`/admin/events/${eventId}/ai/sessions/${sessionId}/transcript`),
};

// Calendar Integration
export const calendarAPI = {
  // iCal downloads
  getEventIcalUrl: (eventId) => `${API_BASE}/events/${eventId}/calendar.ics`,
  getSessionIcalUrl: (eventId, sessionId) => `${API_BASE}/events/${eventId}/sessions/${sessionId}/calendar.ics`,
  
  // Google Calendar links
  getEventGoogleLink: (eventId) => api.get(`/events/${eventId}/calendar/google`),
  getSessionGoogleLink: (eventId, sessionId) => api.get(`/events/${eventId}/sessions/${sessionId}/calendar/google`),
};

// 9. Waitlist Management
export const waitlistAPI = {
  list: (eventId, tierId) => api.get(`/events/${eventId}/waitlist`, { params: { tier_id: tierId } }),
  join: (eventId, data) => api.post(`/events/${eventId}/waitlist`, data),
  update: (eventId, entryId, data) => api.put(`/events/${eventId}/waitlist/${entryId}`, data),
  remove: (eventId, entryId) => api.delete(`/events/${eventId}/waitlist/${entryId}`),
  offer: (eventId, entryId, expiresHours) => api.post(`/events/${eventId}/waitlist/${entryId}/offer`, null, { params: { expires_hours: expiresHours } }),
};

// 10. Promo Codes
export const promoAPI = {
  list: (eventId) => api.get(`/events/${eventId}/promo-codes`),
  get: (eventId, codeId) => api.get(`/events/${eventId}/promo-codes/${codeId}`),
  create: (eventId, data) => api.post(`/events/${eventId}/promo-codes`, data),
  update: (eventId, codeId, data) => api.put(`/events/${eventId}/promo-codes/${codeId}`, data),
  delete: (eventId, codeId) => api.delete(`/events/${eventId}/promo-codes/${codeId}`),
  validate: (eventId, code, tierId) => api.post(`/events/${eventId}/promo-codes/validate`, null, { params: { code, tier_id: tierId } }),
};

// Admin Requests (Public submission, Super Admin review)
export const adminRequestAPI = {
  submit: (data) => api.post('/admin-requests', data),
  list: (status) => api.get('/admin-requests', { params: { status } }),
  get: (requestId) => api.get(`/admin-requests/${requestId}`),
  approve: (requestId, data) => api.post(`/admin-requests/${requestId}/approve`, data),
  reject: (requestId, data) => api.post(`/admin-requests/${requestId}/reject`, data),
};

// Event Moderation (Super Admin)
export const moderationAPI = {
  blockEvent: (eventId, reason) => api.post(`/events/${eventId}/block`, { reason }),
  unblockEvent: (eventId) => api.post(`/events/${eventId}/unblock`),
  closeEvent: (eventId) => api.post(`/events/${eventId}/close`),
};

// Virtual Stage Management
export const virtualStageAPI = {
  // Public endpoints
  list: (eventId) => api.get(`/events/${eventId}/stages`),
  get: (eventId, stageId) => api.get(`/events/${eventId}/stages/${stageId}`),
  
  // Admin endpoints - list uses public route, write ops use admin routes
  adminList: (eventId) => api.get(`/events/${eventId}/stages`),
  create: (eventId, data) => api.post(`/admin/events/${eventId}/stages`, data),
  update: (eventId, stageId, data) => api.put(`/admin/events/${eventId}/stages/${stageId}`, data),
  delete: (eventId, stageId) => api.delete(`/admin/events/${eventId}/stages/${stageId}`),
  
  // Chat
  getChat: (eventId, stageId) => api.get(`/events/${eventId}/stages/${stageId}/chat`),
  sendChat: (eventId, stageId, data) => api.post(`/events/${eventId}/stages/${stageId}/chat`, data),
  pinChat: (eventId, stageId, messageId) => api.post(`/admin/events/${eventId}/stages/${stageId}/chat/${messageId}/pin`),
  
  // Q&A
  getQuestions: (eventId, stageId) => api.get(`/events/${eventId}/stages/${stageId}/questions`),
  submitQuestion: (eventId, stageId, data) => api.post(`/events/${eventId}/stages/${stageId}/questions`, data),
  approveQuestion: (eventId, stageId, questionId) => api.post(`/admin/events/${eventId}/stages/${stageId}/questions/${questionId}/approve`),
  upvoteQuestion: (eventId, stageId, questionId) => api.post(`/events/${eventId}/stages/${stageId}/questions/${questionId}/upvote`),
  
  // Stage control (admin only)
  goLive: (eventId, stageId) => api.post(`/admin/events/${eventId}/stages/${stageId}/go-live`),
  endStream: (eventId, stageId) => api.post(`/admin/events/${eventId}/stages/${stageId}/end`),
};

// WebSocket/Real-time API
export const realtimeAPI = {
  // Polling for events
  poll: (eventId, lastEventId = '0', channels = 'all') => 
    api.get(`/events/${eventId}/ws/poll`, { params: { last_event_id: lastEventId, channels } }),
  
  // Get connection stats
  stats: (eventId) => api.get(`/events/${eventId}/ws/stats`),
  
  // Subscribe to channels
  subscribe: (eventId, clientId, channels) => 
    api.post(`/events/${eventId}/ws/subscribe`, { client_id: clientId, channels }),
  
  // Unsubscribe
  unsubscribe: (eventId, clientId) => 
    api.post(`/events/${eventId}/ws/unsubscribe`, { client_id: clientId }),
  
  // Broadcast event (admin only)
  broadcast: (eventId, type, data, channels = ['all']) => 
    api.post(`/events/${eventId}/ws/broadcast`, { type, data, channels }),
};

// Organisation-owned SMS gateways (iter 620). Every call is organisation
// scoped server-side; the browser never sees an SMS8 key.
export const smsAPI = {
  listGateways: (organisationId) =>
    api.get('/admin/sms/gateways', { params: organisationId ? { organisation_id: organisationId } : {} }),
  getGateway: (id) => api.get(`/admin/sms/gateways/${id}`),
  createGateway: (data) => api.post('/admin/sms/gateways', data),
  updateGateway: (id, data) => api.patch(`/admin/sms/gateways/${id}`, data),
  saveCredentials: (id, data) => api.post(`/admin/sms/gateways/${id}/credentials`, data),
  pair: (id, data) => api.post(`/admin/sms/gateways/${id}/pair`, data),
  startVerify: (id) => api.post(`/admin/sms/gateways/${id}/verify/start`),
  confirmVerify: (id, code) => api.post(`/admin/sms/gateways/${id}/verify/confirm`, { code }),
  lifecycle: (id, action) => api.post(`/admin/sms/gateways/${id}/lifecycle`, { action }),
  delegate: (id, data) => api.post(`/admin/sms/gateways/${id}/delegation`, data),
  messages: (id) => api.get(`/admin/sms/gateways/${id}/messages`),
  usage: () => api.get('/admin/sms/usage'),
  inbox: () => api.get('/admin/sms/inbox'),
  send: (data) => api.post('/admin/sms/send', data),
};

// Membership enquiries that named no church — HQ review pool
export const membershipEnquiryAPI = {
  listUnassigned: (status = 'pending') =>
    api.get('/admin/membership-enquiries', { params: { status } }),
  assign: (enquiryId, churchId) =>
    api.post(`/admin/membership-enquiries/${enquiryId}/assign`, { church_id: churchId }),
};

// Churches/Assemblies API
export const churchesAPI = {
  // Public
  list: () => api.get('/churches'),
  directory: () => api.get('/churches/directory'),
  getChurch: (churchId) => api.get(`/churches/${churchId}`),
  getChurchEvents: (churchId) => api.get(`/churches/${churchId}/events`),
  eventChurches: (eventId) => api.get(`/events/${eventId}/churches`),
  bishopManjoro: () => api.get('/churches/bishop-manjoro'),
  
  // Admin
  adminList: (filters = {}) => api.get('/admin/churches', { params: filters }),
  getLocations: () => api.get('/admin/churches/locations'),
  create: (data) => api.post('/admin/churches', data),
  bulkImport: (data) => api.post('/admin/churches/bulk-import', data),
  get: (churchId) => api.get(`/admin/churches/${churchId}`),
  update: (churchId, data) => api.put(`/admin/churches/${churchId}`, data),
  delete: (churchId) => api.delete(`/admin/churches/${churchId}`),
  reorder: (churchIds) => api.post('/admin/churches/reorder', churchIds),
  seedBishopManjoro: () => api.post('/admin/churches/seed-bishop-manjoro'),
  
  // Event associations
  getEventChurches: (eventId) => api.get(`/admin/events/${eventId}/churches`),
  updateEventChurches: (eventId, churchIds) => api.put(`/admin/events/${eventId}/churches`, { church_ids: churchIds }),
};

// Institutions API (hospitals, schools, etc.)
export const institutionsAPI = {
  // Public
  list: () => api.get('/institutions'),
  directory: () => api.get('/institutions/directory'),
  getInstitution: (institutionId) => api.get(`/institutions/${institutionId}`),
  eventInstitutions: (eventId) => api.get(`/events/${eventId}/institutions`),
  
  // Admin
  adminList: (filters = {}) => api.get('/admin/institutions', { params: filters }),
  getLocations: () => api.get('/admin/institutions/locations'),
  getTypes: () => api.get('/admin/institutions/types'),
  create: (data) => api.post('/admin/institutions', data),
  get: (institutionId) => api.get(`/admin/institutions/${institutionId}`),
  update: (institutionId, data) => api.put(`/admin/institutions/${institutionId}`, data),
  delete: (institutionId) => api.delete(`/admin/institutions/${institutionId}`),
  reorder: (institutionIds) => api.post('/admin/institutions/reorder', institutionIds),
  
  // Event associations
  getEventInstitutions: (eventId) => api.get(`/admin/events/${eventId}/institutions`),
  updateEventInstitutions: (eventId, institutionIds) => api.put(`/admin/events/${eventId}/institutions`, { institution_ids: institutionIds }),
};

// CMS Pages API
export const cmsAPI = {
  // Public
  listPages: () => api.get('/pages'),
  getPage: (slug) => api.get(`/pages/${slug}`),
  
  // Admin
  getBlockTypes: () => api.get('/admin/cms/block-types'),
  getTemplates: () => api.get('/admin/cms/templates'),
  adminListPages: (isPublished, pageType) => api.get('/admin/cms/pages', { params: { is_published: isPublished, page_type: pageType } }),
  adminGetPage: (pageId) => api.get(`/admin/cms/pages/${pageId}`),
  createPage: (data) => api.post('/admin/cms/pages', data),
  updatePage: (pageId, data) => api.put(`/admin/cms/pages/${pageId}`, data),
  deletePage: (pageId) => api.delete(`/admin/cms/pages/${pageId}`),
  duplicatePage: (pageId) => api.post(`/admin/cms/pages/${pageId}/duplicate`),
  updateBlocks: (pageId, blocks) => api.put(`/admin/cms/pages/${pageId}/blocks`, blocks),
  createFromTemplate: (templateId, title, slug) => api.post('/admin/cms/pages/from-template', null, { params: { template_id: templateId, title, slug } }),
};

// Landing Pages Configuration API
export const landingPagesAPI = {
  // Public - get configurations
  getAll: () => api.get('/landing-pages'),
  getPage: (pageId) => api.get(`/landing-pages/${pageId}`),
  
  // Admin - update configurations
  updatePage: (pageId, data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    return api.post(`/admin/landing-pages/${pageId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateFeatures: (pageId, features) => {
    const formData = new FormData();
    formData.append('features', JSON.stringify(features));
    return api.post(`/admin/landing-pages/${pageId}/features`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  resetPage: (pageId) => api.post(`/admin/landing-pages/${pageId}/reset`),
};

export default api;
